import { useState, useEffect } from 'react';
import { getVisibleRichText } from '@/utils/format';
import TextEditorComponent, { TextEditorProps } from './TextEditor.component';

type TextEditorType = Pick<
  TextEditorProps,
  | 'isEditing'
  | 'hideButtons'
  | 'isDisabled'
  | 'hideEditButton'
  | 'value'
  | 'errorMessage'
  | 'markdownClassNames'
> & {
  onSaveText?: (value: string) => void;
  onChangeText?: (value: string) => void;
};

function TextEditor({
  value: defaultValue,
  isEditing,
  hideButtons = false,
  hideEditButton = false,
  onSaveText,
  onChangeText,
  ...props
}: TextEditorType): JSX.Element {
  const [textEditorState, setTextEditorState] = useState({
    value: defaultValue,
    prevValue: defaultValue,
    isEditing: isEditing || !getVisibleRichText(defaultValue),
  });

  useEffect(() => {
    setTextEditorState((prev) => ({
      prevValue: !defaultValue ? prev.value : prev.prevValue,
      value: defaultValue,
      isEditing: !defaultValue || prev.isEditing,
    }));
  }, [defaultValue]);

  useEffect(() => {
    setTextEditorState((prev) => ({
      ...prev,
      isEditing: isEditing ?? prev.isEditing,
    }));
  }, [isEditing]);

  const onChangeValue = (newValue: any): void => {
    setTextEditorState((prev) => ({
      ...prev,
      value: newValue,
    }));

    if (onChangeText) {
      onChangeText(newValue);
    }
  };

  const onEditContent = (): void => {
    setTextEditorState((prev) => ({
      ...prev,
      isEditing: true,
    }));
  };

  const onSaveContent = async (): Promise<void> => {
    const normalizedValue = getVisibleRichText(textEditorState.value);
    const hasContent = !!normalizedValue;

    if (onSaveText) {
      await onSaveText(hasContent ? textEditorState.value : '');
    }

    setTextEditorState((prev) => ({
      value: hasContent ? prev.value : '',
      prevValue: hasContent ? prev.value : '',
      isEditing: !prev.value || !hasContent,
    }));
  };

  const onCancelContentEdition = (): void => {
    setTextEditorState((prev) => ({
      ...prev,
      isEditing: !prev.prevValue,
      value: prev.prevValue,
    }));
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-color-mode', 'light');
  }, [textEditorState.isEditing]);

  return (
    <TextEditorComponent
      onSaveContent={onSaveContent}
      isEditing={textEditorState.isEditing}
      onEditContent={onEditContent}
      onCancelContentEdition={onCancelContentEdition}
      onChangeValue={onChangeValue}
      value={textEditorState.value}
      hideButtons={
        hideButtons ||
        !(
          getVisibleRichText(textEditorState.value) || getVisibleRichText(textEditorState.prevValue)
        )
      }
      hideEditButton={hideEditButton}
      {...props}
    />
  );
}

export default TextEditor;
